import {
    getDraft,
    getDraftPreview,
    getTasksByDraftId,
} from "@/entities/DataAssociation/DataAssociation.queries";
import { useSelectedFile } from "./useSelectedFile";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import {
    AccountHintType,
    DraftStatus,
    DraftAccount,
} from "@/entities/DataAssociation/Draft.model";
import { useRouter } from "@/utils";
import { draftEditsAtom } from "../Labeling.state";
import {
    DocumentType,
    StatementType,
} from "@/entities/DataAssociation/File.model";
import { ISLabel, BSLabel } from "@/entities/DataAssociation/Account.model";
import { TaskStatus } from "@/entities/DataAssociation/Task.model";

export function useDraft() {
    const { selectedFileId, selectedFile } = useSelectedFile();
    const [draftEdits, setDraftEdits] = useAtom(draftEditsAtom);
    const router = useRouter();
    const isReadonly = useMemo(
        () =>
            !router.params.draftId ||
            selectedFile?.documentType === DocumentType.LABELED,
        [router.params.draftId, selectedFile?.documentType],
    );
    const draftPreview = useApiQuery(getDraftPreview, {
        enabled: !!selectedFileId && isReadonly,
    });
    const draft = useApiQuery(getDraft, {
        enabled: !!selectedFileId && !isReadonly,
    });
    const tasks = useApiQuery(getTasksByDraftId, {
        enabled: !!router.params.draftId,
    });

    const currentDraft = useMemo(
        () => (isReadonly ? draftPreview : draft),
        [selectedFileId, isReadonly, draftPreview.data?.id, draft.data],
    );

    useEffect(() => {
        const finishedTask = tasks.data?.find(
            (task) => task.status === TaskStatus.Completed,
        );
        if (finishedTask) {
            router.navigate(
                `/beta/companies/${router.params.companyId}/labeling/files/${finishedTask.resultFileId}`,
            );
        }
    }, [tasks.data, router.params.companyId, router.navigate]);

    const applyAccount = useCallback(
        (rowId: string, draftAccount: DraftAccount) => {
            const row = currentDraft.data?.rows?.[rowId];
            if (!row || !draftAccount) return;

            setDraftEdits((prev) => {
                const account: DraftAccount = {
                    type: draftAccount.type,
                    accountName: draftAccount.accountName,
                    label: draftAccount.label,
                    meta: row.account?.meta || null,
                };

                if (
                    account.type === AccountHintType.EXISTING &&
                    draftAccount.accountId
                ) {
                    account.accountId = draftAccount.accountId;
                }

                return {
                    rows: {
                        ...(prev.rows || {}),
                        [rowId]: {
                            ...(prev.rows?.[rowId] || {}),
                            account,
                        },
                    },
                };
            });
        },
        [currentDraft.data?.id, setDraftEdits],
    );

    const draftWithEdits = useMemo(() => {
        if (isReadonly || !currentDraft.data) {
            return currentDraft.data;
        }

        const mergedRows = { ...currentDraft.data.rows };

        if (draftEdits.rows) {
            Object.entries(draftEdits.rows).forEach(([rowId, rowEdit]) => {
                if (mergedRows[rowId]) {
                    mergedRows[rowId] = {
                        ...mergedRows[rowId],
                        ...rowEdit,
                    };
                }
            });
        }

        return {
            rows: mergedRows,
        };
    }, [currentDraft.data?.id, draftEdits, isReadonly]);

    const derivedStatementTypes = useMemo(() => {
        if (!currentDraft.data?.rows) {
            return [];
        }

        const statementTypes = new Set<StatementType>();

        Object.values(currentDraft.data.rows).forEach((row) => {
            if (!row.account?.label) return;

            const label = row.account.label;

            if (Object.values(ISLabel).includes(label as ISLabel)) {
                statementTypes.add(StatementType.INCOME_STATEMENT);
            } else if (Object.values(BSLabel).includes(label as BSLabel)) {
                statementTypes.add(StatementType.BALANCE_SHEET);
            }
        });

        return Array.from(statementTypes);
    }, [currentDraft.data?.rows]);

    return {
        draft: currentDraft,
        draftEdits,
        applyAccount,
        isReadonly:
            isReadonly || currentDraft.data?.status === DraftStatus.COMMITTED,
        draftWithEdits,
        isCodat: currentDraft.data?.identifierSource === "codat",
        isFile: currentDraft.data?.identifierSource === "file",
        derivedStatementTypes,
    };
}
